/**
 * Predictive Search
 * Live search suggestions with product thumbnails, keyboard navigation,
 * debounced fetch, and result caching.
 */

class PredictiveSearch {
    constructor() {
        this.containers = document.querySelectorAll('[data-predictive-search]');
        if (this.containers.length === 0) return;

        this.debounceTimer = null;
        this.abortController = null;
        this.cachedResults = {};
        this.themeConfig = window.__theme || {};

        this.init();
    }

    init() {
        this.containers.forEach(container => {
            const input = container.querySelector('[data-predictive-search-input]');
            const results = container.querySelector('[data-predictive-search-results]');
            if (!input || !results) return;

            input.addEventListener('input', (e) => this.onInput(e, container));
            input.addEventListener('focus', (e) => this.onFocus(e, container));
            input.addEventListener('keydown', (e) => this.onKeydown(e, container));
        });

        // Single document-level click listener for closing results
        document.addEventListener('click', (e) => {
            this.containers.forEach(container => {
                if (!container.contains(e.target)) {
                    const results = container.querySelector('[data-predictive-search-results]');
                    if (results) this.hideResults(results);
                }
            });
        });
    }

    /* ---------------------------------------------------
       Input events
       --------------------------------------------------- */

    onInput(event, container) {
        const query = event.target.value.trim();
        const results = container.querySelector('[data-predictive-search-results]');

        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        if (this.abortController) this.abortController.abort();

        if (query.length < 2) {
            this.hideResults(results);
            return;
        }

        // Update "view all" link
        const viewAllLink = container.querySelector('[data-predictive-search-view-all]');
        if (viewAllLink) viewAllLink.href = '/search?q=' + encodeURIComponent(query);

        this.setResultsState(results, 'loading');

        this.debounceTimer = setTimeout(() => this.search(query, container), 300);
    }

    onFocus(event, container) {
        const query = event.target.value.trim();
        const results = container.querySelector('[data-predictive-search-results]');

        if (query.length >= 2 && this.cachedResults[query]) {
            this.renderResults(this.cachedResults[query], container);
            this.setResultsState(results, 'results');
        }
    }

    /* ---------------------------------------------------
       Keyboard navigation
       --------------------------------------------------- */

    onKeydown(event, container) {
        const results = container.querySelector('[data-predictive-search-results]');
        const items = results.querySelectorAll('.predictive-search__item');
        if (items.length === 0) return;

        const activeItem = results.querySelector('.predictive-search__item--active');
        const activeIndex = Array.from(items).indexOf(activeItem);

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (activeIndex < items.length - 1) this.setActiveItem(items, activeIndex + 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (activeIndex > 0) this.setActiveItem(items, activeIndex - 1);
                break;
            case 'Enter':
                if (activeItem) {
                    event.preventDefault();
                    const link = activeItem.querySelector('a');
                    if (link) link.click();
                }
                break;
            case 'Escape':
                this.hideResults(results);
                break;
        }
    }

    setActiveItem(items, index) {
        items.forEach((item, i) => {
            item.classList.toggle('predictive-search__item--active', i === index);
        });
    }

    /* ---------------------------------------------------
       Search + render
       --------------------------------------------------- */

    async search(query, container) {
        const results = container.querySelector('[data-predictive-search-results]');

        if (this.cachedResults[query]) {
            this.renderResults(this.cachedResults[query], container);
            this.setResultsState(results, 'results');
            return;
        }

        this.abortController = new AbortController();

        try {
            const response = await fetch(
                '/search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=6',
                { signal: this.abortController.signal }
            );

            if (!response.ok) throw new Error('Search failed');

            const data = await response.json();
            this.cachedResults[query] = data;
            this.renderResults(data, container);
            this.setResultsState(results, 'results');
        } catch (error) {
            if (error.name !== 'AbortError') this.hideResults(results);
        }
    }

    renderResults(data, container) {
        const productsContainer = container.querySelector('.predictive-search__products');
        const products = data.resources?.results?.products || [];

        if (products.length === 0) {
            const noResultsText = this.themeConfig.strings?.noProductsFound || 'No products found';
            productsContainer.innerHTML = '<div class="predictive-search__no-results">' + noResultsText + '</div>';
            return;
        }

        productsContainer.innerHTML = products.map((product, index) => `
            <div class="predictive-search__item ${index === 0 ? 'predictive-search__item--active' : ''}">
                <a href="${product.url}" class="predictive-search__link">
                    <div class="predictive-search__image">
                        ${product.image
                            ? '<img src="' + product.image + '" alt="' + product.title + '" loading="lazy" onerror="this.style.display=\'none\'">'
                            : '<div class="predictive-search__placeholder"></div>'}
                    </div>
                    <div class="predictive-search__info">
                        <span class="predictive-search__title">${product.title}</span>
                        <span class="predictive-search__price">${this.formatMoney(product.price)}</span>
                    </div>
                </a>
            </div>
        `).join('');
    }

    formatMoney(price) {
        return new Intl.NumberFormat(this.themeConfig.locale || 'en', {
            style: 'currency',
            currency: this.themeConfig.currency || 'EUR',
        }).format(parseFloat(price));
    }

    /* ---------------------------------------------------
       Results state management
       --------------------------------------------------- */

    /**
     * Show the results panel in a given state: 'loading' or 'results'.
     */
    setResultsState(results, state) {
        results.hidden = false;
        const loading = results.querySelector('.predictive-search__loading');
        const products = results.querySelector('.predictive-search__products');
        const viewAll = results.querySelector('.predictive-search__view-all');

        const isLoading = state === 'loading';
        if (loading) loading.style.display = isLoading ? 'flex' : 'none';
        if (products) products.style.display = isLoading ? 'none' : 'block';
        if (viewAll) viewAll.style.display = isLoading ? 'none' : 'block';
    }

    hideResults(results) {
        results.hidden = true;
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new PredictiveSearch();
});
